'use client';

import { useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Link from 'next/link';

type BillingPeriod = 'monthly' | 'annually';

const plan = {
  name: 'Standard',
  monthlyPrice: 2999,
  annualPrice: 21999,
  description: 'Everything you need to run your catering business',
  features: [
    'Up to 5 users',
    'Up to 150 products',
    'Up to 100 inventory items',
    'All features included',
  ],
};

const currency = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

// Per-month price when billed annually
const annualPerMonth = Math.round(plan.annualPrice / 12);
// Savings vs paying monthly for a full year
const annualSavingsPercent = Math.round(
  (1 - plan.annualPrice / (plan.monthlyPrice * 12)) * 100
);

export function PricingSection() {
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('annually');
  const isAnnual = billingPeriod === 'annually';

  const displayPrice = isAnnual ? annualPerMonth : plan.monthlyPrice;

  return (
    <section className="py-20 px-6">
      <div className="container max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <Badge variant="secondary" className="mb-4">
            Pricing
          </Badge>
          <h2 className="text-3xl md:text-4xl font-normal mb-4">
            Simple, transparent pricing
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Start with our 7-day free trial, then continue with a plan that fits your business.
          </p>
        </div>

        {/* Billing toggle */}
        <div className="flex items-center justify-center gap-3 mb-12">
          <div className="inline-flex items-center rounded-full border border-gray-200 p-1 bg-muted/40">
            <button
              type="button"
              onClick={() => setBillingPeriod('annually')}
              className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                isAnnual
                  ? 'bg-white text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Annually
              <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                Save {annualSavingsPercent}%
              </span>
            </button>
            <button
              type="button"
              onClick={() => setBillingPeriod('monthly')}
              className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                !isAnnual
                  ? 'bg-white text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Monthly
            </button>
          </div>
        </div>

        {/* Pricing Card */}
        <div className="max-w-md mx-auto">
          <Card className="relative rounded-xl border-green-200 shadow-lg">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <Badge className="bg-green-600 text-white px-4 py-1">
                Most Popular
              </Badge>
            </div>

            <CardHeader className="text-center pb-8">
              <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
              <div className="mb-2">
                <span className="text-4xl font-normal">
                  {currency.format(displayPrice)}
                </span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <p className="text-sm text-muted-foreground">
                {isAnnual
                  ? `Billed annually at ${currency.format(plan.annualPrice)}`
                  : 'Billed monthly'}
              </p>
              <p className="text-xs text-muted-foreground mt-3">
                {plan.description}
              </p>
            </CardHeader>

            <CardContent>
              <Link href="/signup" className="block mb-8">
                <Button className="w-full rounded-full bg-green-600 hover:bg-green-500">
                  Start Free Trial
                </Button>
              </Link>

              <ul className="space-y-3">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex text-gray-600 items-start gap-3">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
